import { createApi } from "@reduxjs/toolkit/query/react";
import { sharedBaseQuery } from "../../api/baseQuery";

export type SettlementDeviceType =
  | "building_settlement"
  | "ground_settlement"
  | "utility_settlement"
  | "movement"
  | "ground_movement"
  | "building_movement"
  | "utility_movement";

export interface DistortionPairRecord {
  id: string;
  projectId: number;
  pointADeviceId: string;
  pointBDeviceId: string;
  pairLabel: string | null;
  alertProfileId?: number | null;
  alertProfileDescription?: string | null;
  baselineDistanceM: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  pointAName?: string;
  pointBName?: string;
  pointAType?: SettlementDeviceType;
  pointBType?: SettlementDeviceType;
}

export interface CreateDistortionPairDto {
  projectId: number;
  pointADeviceId: string;
  pointBDeviceId: string;
  pairLabel?: string;
  alertProfileId?: number | null;
}

export interface UpdateDistortionPairDto {
  pairLabel?: string | null;
  isActive?: boolean;
  alertProfileId?: number | null;
}

export interface CalculatedDistortionPair {
  pair: DistortionPairRecord;
  differentialSettlement: number | null;
  distortion: number | null;
  distortionRatio: string | null;
  status: "ok" | "missing_reading" | "invalid_distance";
}

export const distortionPairsApi = createApi({
  reducerPath: "distortionPairsApi",
  baseQuery: sharedBaseQuery.baseQueryWithReauth,
  tagTypes: ["DistortionPair"],
  endpoints: (builder) => ({
    getDistortionPairs: builder.query<DistortionPairRecord[], number>({
      query: (projectId) => ({
        url: `distortion-pairs?projectId=${projectId}&includeInactive=true`,
      }),
      providesTags: ["DistortionPair"],
    }),
    createDistortionPair: builder.mutation<
      DistortionPairRecord,
      CreateDistortionPairDto
    >({
      query: (body) => ({ url: "distortion-pairs", method: "POST", body }),
      invalidatesTags: ["DistortionPair"],
    }),
    updateDistortionPair: builder.mutation<
      DistortionPairRecord,
      { id: string; body: UpdateDistortionPairDto }
    >({
      query: ({ id, body }) => ({
        url: `distortion-pairs/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["DistortionPair"],
    }),
    deleteDistortionPair: builder.mutation<void, string>({
      query: (id) => ({
        url: `distortion-pairs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DistortionPair"],
    }),
    getCalculatedDistortions: builder.query<CalculatedDistortionPair[], number>(
      {
        query: (projectId) => ({
          url: `distortion-pairs/calculated?projectId=${projectId}`,
        }),
        providesTags: ["DistortionPair"],
      },
    ),
  }),
});

export const {
  useGetDistortionPairsQuery,
  useCreateDistortionPairMutation,
  useUpdateDistortionPairMutation,
  useDeleteDistortionPairMutation,
  useGetCalculatedDistortionsQuery,
} = distortionPairsApi;
